import { cn } from '@invent/wl-ui-kit-next';
import { ArrowLeft } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import type { IListCriteriaGroup } from '../api/lists-contracts';
import type { IRegistrationQuery, RecordType } from '../api/participant-contracts';
import { TEAM_PATHS } from '../paths';

/**
 * A participant's name as the way to their record page (`/team/people/:participantId`, SR-215 —
 * and since SR-414 a team's under `/team/teams/…`, a firm's or org's under `/team/firms/…`; the
 * participant card's `/admin/participants/:participantId` before it redirects there) — one
 * component for every screen that names somebody: a People row, a response, a registration run,
 * a user card's participants, the SEC ADV firm card's participants, and a hub's own household
 * and accounts. **This is the one place the path is spelled**,
 * which is how the card's move to the hub, and the search's to People, changed every way to
 * them at once — the list's path, and the old search's query translated, are here too.
 *
 * One rendering so that the hub is reached the same way from everywhere, and so that the one
 * piece of state a link carries is carried by all of them alike: **the search it came from.**
 * The People list keeps its whole query in the URL, and a hub opened from it sends that query
 * along in the history entry's state, so the hub's way back returns to the search rather than
 * to an empty list. Only the list has a search to send; every other link sends none, and the
 * hub's way back is then the plain list. Browser history restores the search on its own either
 * way — this is for the link on the page.
 *
 * The id is a server-issued GUID and is encoded anyway, for the reason `participants-api.ts`
 * gives: a path segment is built from it.
 *
 * What the link and the hub's way back need is here too, and shared with the SEC ADV register's
 * firm card (`FirmLink`): `CardLink`, the state it leaves (`ICardLinkState`), `searchFrom`, which
 * reads it back, and `BackToList`. They keep the card's names from before the hub; "a card"
 * below is either a hub or a firm card.
 */

/** The People list (SR-215) — the hubs' list, and every person's way back. */
export const PEOPLE_PATH = TEAM_PATHS.people;

/**
 * The three lists a record page sits beside (SR-414, the owner's Q2): People's persons, Teams and
 * Firms — a firm and any other organisation alike. Which one is the record's type's, and a link
 * that does not know the type names People: the record page, once the hub has said what the
 * participant is, replaces such an address with its own (the plan's D6).
 */
export type RecordArea = 'people' | 'teams' | 'firms';

export const RECORD_AREA_PATH: Readonly<Record<RecordArea, string>> = {
  people: TEAM_PATHS.people,
  teams: TEAM_PATHS.teams,
  firms: TEAM_PATHS.firms
};

/** Each list's name, as a crumb and a pane call it. */
export const RECORD_AREA_LABEL: Readonly<Record<RecordArea, string>> = {
  people: 'People',
  teams: 'Teams',
  firms: 'Firms'
};

/** A record type in words — a record tab's label, a picker's row, a search result's type
 *  (SR-414). */
export const RECORD_TYPE_WORDS: Readonly<Record<RecordType, string>> = {
  person: 'Person',
  team: 'Team',
  firm: 'Firm',
  org: 'Organization'
};

/** What a link knows of the record it opens — its record type, or nothing. A participant's
 *  `kind` does not say it: an `org` is a team, a firm or neither, by survey-api's rule. */
export interface IRecordRef {
  recordType?: Nullable<string>;
}

/** The list a record belongs under: a `team` Teams, a `firm` or `org` Firms, anything else —
 *  a person, or a type the link does not know — People. */
export function recordAreaOf({ recordType }: IRecordRef = {}): RecordArea {
  if (recordType === 'team') return 'teams';
  if (recordType === 'firm' || recordType === 'org') return 'firms';
  return 'people';
}

/** A record page under a given list — the record page's own tabs, which are drawn under the
 *  list the page is open beside. **The one place a record's address is spelled.** */
export function recordPathIn(area: RecordArea, participantId: string): string {
  return `${RECORD_AREA_PATH[area]}/${encodeURIComponent(participantId)}`;
}

/** A record page, under the list its type names (`recordAreaOf`). */
export function recordPath(participantId: string, ref?: IRecordRef): string {
  return recordPathIn(recordAreaOf(ref), participantId);
}

/**
 * The search a link carries from a record page under one list to a record under another — **its
 * `q` alone** (SR-414, gate 2). A list's other parameters are its own: People's `sort=last_login`
 * or `account=unregistered` is survey-api's 400 on Teams and Firms, and People's `verification`
 * means a team's own numbers there. The words searched mean the same on every list (the strip's
 * rule), so they cross; a link inside one list carries its search whole. `?` included, or `''`.
 */
export function searchAcross(search: string, from: Nullable<RecordArea>, to: RecordArea): string {
  if (from === null || from === to) return search;
  const q = new URLSearchParams(search).get('q');
  return q ? `?${new URLSearchParams({ q }).toString()}` : '';
}

/**
 * The list the page a link sits on is drawn under — a record page's, or a roster card's
 * (People) — so `ParticipantLink` carries a search across lists by `searchAcross`. `null`
 * everywhere else: a list's own links carry none across.
 */
export const RecordAreaContext = React.createContext<Nullable<RecordArea>>(null);

/** A record page under People — a person's, or one whose type the link does not know. */
export function personHubPath(participantId: string): string {
  return recordPath(participantId);
}

/** A record page's Account tab (SR-391) — the logins acting for the record, how they sign in and
 *  the registration runs, apart from the record's own details, as Anton set out to the client on
 *  2026-10-08 ("one tab for user-admin functions"). */
export function recordAccountPath(participantId: string, area: RecordArea = 'people'): string {
  return `${recordPathIn(area, participantId)}/account`;
}

/** A record's survey tab (SR-391) — the record page's tab for one survey the record holds, a
 *  path segment under the record's own address so a copied tab still lands on it. */
export function recordSurveyPath(
  participantId: string,
  surveyCode: string,
  area: RecordArea = 'people'
): string {
  return `${recordPathIn(area, participantId)}/surveys/${encodeURIComponent(surveyCode)}`;
}

/**
 * An unregistered member's card (SR-414, record search §3.6), beside People — by its roster key
 * (`crd:…`, `email:…`, `slot:…`), encoded as one segment. React Router hands the parameter back
 * decoded and it is never decoded again; the API call encodes it its own way
 * (`participants-api.ts`' `getRosterCard`), which is a different rule for a different URL — the
 * user card's precedent.
 */
export function rosterCardPath(rosterKey: string): string {
  return `${PEOPLE_PATH}/roster/${encodeURIComponent(rosterKey)}`;
}

/**
 * The participant search's query (`/admin/participants`, before SR-215) as the People list's —
 * for a pasted or bookmarked search, and for the search a participant card opened from it
 * carried in its history state. `q` is kept as typed; `denied=true` is `verification=denied` and
 * `hasAccount=false` is `account=none`, the same two queues in the list's vocabulary. `kind` is
 * dropped, because People has no kind filter — a row is an account with its person, firm and
 * team together (the plan's Q2) — and so are `page`, a page of a different list, and
 * `hasAccount=true`, which People has no filter for. `?` included, or `''` when nothing is left.
 */
export function peopleSearchOf(participantsSearch: string): string {
  const old = new URLSearchParams(participantsSearch);
  const next = new URLSearchParams();
  const q = old.get('q');
  if (q !== null && q !== '') next.set('q', q);
  if (old.get('denied') === 'true') next.set('verification', 'denied');
  if (old.get('hasAccount') === 'false') next.set('account', 'none');
  const rendered = next.toString();
  return rendered ? `?${rendered}` : '';
}

/** What a link to a card leaves in the history entry — a participant's hub, and a register
 *  firm's card (`FirmLink`), one shape so that `searchFrom` reads either. */
export interface ICardLinkState {
  /** The list's `location.search`, `?` included — the People list's or the firm list's. */
  search: string;
  /** Left by the list itself, whose card is then drawn beside it (`SplitView`). */
  list?: true;
  /** Left by the search results page (SR-463): `search` is that page's, and the card's way back
   *  is to it rather than to its own list. */
  results?: true;
  /** Left by a list whose rows open another area's records (SR-530 — Reinstatement, Registrations,
   *  Search results, the recycle bin): the record is drawn beside that list, not its own area's. */
  origin?: IListOrigin;
}

/**
 * The list a record was opened from, where it is not the record's own area's (SR-530): its title,
 * the way back to it under its search and filters, which list page draws under a record's sheet,
 * and its rows as drawn when the record was opened — the column beside the record, walked by ↑ / ↓
 * and *Previous* / *Next*. The rows are the page the reader saw, not read again: Back returns to the
 * live list.
 */
export interface IListOrigin {
  page: ListOriginPage;
  title: string;
  /** The list's address, its search and filters included. */
  back: string;
  items: IListOriginItem[];
  /** How to ask the list, after a write on a record opened from it, whether the record is still
   *  on it — its row then "done" in the column (`stillListed`); none where a record's write cannot
   *  take it off the list (Search results). */
  recheck?: IOriginRecheck;
}

/** The list's own question, kept to ask it of one record again — the recycle bin's is whether the
 *  response is still `recycled`, which its filters cannot change. */
export type IOriginRecheck =
  | { kind: 'lists'; survey: string; criteria: IListCriteriaGroup }
  | { kind: 'registrations'; query: IRegistrationQuery }
  | { kind: 'bin' };

/** The lists whose rows open other areas' records. */
export type ListOriginPage = 'reinstatement' | 'registrations' | 'search' | 'recycle-bin';

export interface IListOriginItem {
  key: string;
  /** The record's address. */
  to: string;
  primary: string;
  secondary?: string;
}

/** The history state a row of such a list leaves: the record is drawn beside that list. */
export function originState(origin: IListOrigin): ICardLinkState {
  return { search: '', list: true, origin };
}

/** The list a record was opened from, where it is another area's (`originState`). */
export function originOf(state: unknown): Nullable<IListOrigin> {
  const origin = (state as Partial<ICardLinkState> | null)?.origin;
  return origin && typeof origin === 'object' && Array.isArray(origin.items) && typeof origin.back === 'string'
    ? origin
    : null;
}

/** The origin's row that names the record at `pathname`: its address, one the record's tab is under
 *  (`/team/people/:id/account` under `/team/people/:id`), or one under the record's root (a row
 *  opening its survey tab, the record shown at its root). */
export function originRowAt(origin: IListOrigin, pathname: string): Nullable<IListOriginItem> {
  let best: Nullable<IListOriginItem> = null;
  for (const item of origin.items) {
    const path = item.to.split(/[?#]/)[0];
    const under = pathname === path || pathname.startsWith(`${path}/`) || path.startsWith(`${pathname}/`);
    if (under && (!best || path.length > best.to.split(/[?#]/)[0].length)) {
      best = item;
    }
  }
  return best;
}

/** Whether a card was opened from the search results page — its way back is that page. */
export function openedFromResults(state: unknown): boolean {
  return (state as Partial<ICardLinkState> | null)?.results === true;
}

/** Where a card's way back leads: the search results page under `search` where it was opened
 *  from there, else its own list; and the words of the way back. */
export function wayBack(
  state: unknown,
  list: string,
  listLabel: string,
  search: string
): { to: string; words: string } {
  const origin = originOf(state);
  if (origin) return { to: origin.back, words: `${origin.title} · back to the list` };
  if (openedFromResults(state)) {
    return { to: `${TEAM_PATHS.search}${search}`, words: 'Search results · back to the search' };
  }
  return { to: `${list}${search}`, words: search ? `${listLabel} · back to the search` : listLabel };
}

/** The search results page's address as a list's search (SR-463): its words and cycle alone —
 *  never its card or window, which no list reads. `''` without words. */
export function resultsListSearch(search: string): string {
  const from = new URLSearchParams(search);
  const next = new URLSearchParams();
  for (const name of ['q', 'cycle']) {
    const value = from.get(name);
    if (value) next.set(name, value);
  }
  const rendered = next.toString();
  return rendered ? `?${rendered}` : '';
}

/**
 * The search results page's query, for every card link drawn inside it (SR-463): a list's own
 * columns, drawn there, link as on their list, and the link leaves `{search, results}` instead,
 * so the card returns to the results. Null everywhere else.
 */
export const SearchResultsContext = React.createContext<Nullable<string>>(null);

/**
 * The search a card was opened from, or `''`. History state is whatever the entry carries —
 * another screen's, or nothing after a pasted link — so only a string that is a query string
 * is taken.
 */
export function searchFrom(state: unknown): string {
  const search = (state as Partial<ICardLinkState> | null)?.search;
  return typeof search === 'string' && search.startsWith('?') ? search : '';
}

/** Whether a card was opened from its own list — the one entry that has the card drawn beside
 *  the list, rather than full width. */
export function openedFromList(state: unknown): boolean {
  return (state as Partial<ICardLinkState> | null)?.list === true;
}

/**
 * A link to a card, carrying the list's search in its history state when it has one — the shape
 * `ParticipantLink` and `FirmLink` share, so the hub and the firm card are reached, and left,
 * alike.
 */
export function CardLink({
  to,
  search,
  fromList,
  className,
  children
}: {
  to: string;
  /** The list's own query, when the link is on the list. */
  search?: string;
  /** The link is on the list, whose card is then drawn beside it. */
  fromList?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  const results = React.useContext(SearchResultsContext);
  const state: ICardLinkState | undefined =
    results !== null && (fromList || search)
      ? { search: results, results: true }
      : fromList
        ? { search: search ?? '', list: true }
        : search
          ? { search }
          : undefined;
  return (
    <Link
      to={to}
      state={state}
      className={cn(
        'font-medium text-[var(--color-sapphire)] underline-offset-4 hover:underline',
        className
      )}
    >
      {children}
    </Link>
  );
}

/**
 * A card's way back: to the list's search it was opened from — "Back to the search" — or, opened
 * from anywhere else, to the plain list, named by `all`.
 */
/** The way back's words to a list whose title reads badly after "Back to". */
const BACK_WORDS: Partial<Record<ListOriginPage, string>> = {
  search: 'Back to the search results',
  'recycle-bin': 'Back to the recycle bin'
};

export function BackToList({
  list,
  search,
  all,
  state
}: {
  list: string;
  search: string;
  all: string;
  /** The entry's history state: a card opened from the search results page, or from another
   *  area's list (`IListOrigin`), returns there. */
  state?: unknown;
}) {
  const origin = originOf(state);
  const results = openedFromResults(state);
  return (
    <Link
      to={origin ? origin.back : results ? `${TEAM_PATHS.search}${search}` : `${list}${search}`}
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-[var(--color-royal-blue)]"
    >
      <ArrowLeft className="size-4" aria-hidden />
      {origin
        ? (BACK_WORDS[origin.page] ?? `Back to ${origin.title}`)
        : results
          ? 'Back to the search results'
          : search
            ? 'Back to the search'
            : all}
    </Link>
  );
}

export function ParticipantLink({
  participant,
  search,
  fromList,
  className
}: {
  /** The record, and its `recordType` where the caller's row carries one, so the link names its
   *  list (the plan's D7); without it the link names People, and the record page moves itself. */
  participant: { participantId: string; displayName: string } & IRecordRef;
  /** The list's own query, when the link is on the list. */
  search?: string;
  fromList?: boolean;
  className?: string;
}) {
  const from = React.useContext(RecordAreaContext);
  return (
    <CardLink
      to={recordPath(participant.participantId, participant)}
      search={search && searchAcross(search, from, recordAreaOf(participant))}
      fromList={fromList}
      className={className}
    >
      {participant.displayName}
    </CardLink>
  );
}
